import { EventEmitter, Injectable } from "@angular/core";
import { GithubService } from "../../services/github.service";
import _ from "lodash";
import { Node } from "../../models/node.model";

@Injectable({
  providedIn: "root",
})
export class MarkdownEditorService {
  private static instance: MarkdownEditorService;
  private markdown: string;
  private path: string;
  private sha: string;
  private node: Node;
  config = new Config();
  unsavedChanges = [];
  markdownChanged = new EventEmitter<string>();
  wiki = new EventEmitter<any>();

  public setNode(node: Node) {
    this.node = node;
  }

  public getPath() {
    return this.path;
  }

  public getSha() {
    return this.sha;
  }

  public getNode() {
    if (!this.node.name) {
      this.node.name = this.path.split("/").pop();
    }
    return this.node;
  }

  constructor(public githubService: GithubService) {
    // Private constructor to prevent direct instantiation
    this.githubService
      .getFileContent(".gitten/config.json")?.then((data: any) => {
        if (data) {
          this.config = JSON.parse(data) as Config;
        } else {
          this.saveConfig();
        }
      });
  }

  public setMarkdown(markdown: string): void {
    this.markdownChanged.emit(markdown);
    this.markdown = markdown;
  }

  public getMarkdown(): string {
    return this.markdown;
  }

  public static getInstance(
    githubService: GithubService
  ): MarkdownEditorService {
    if (!MarkdownEditorService.instance) {
      MarkdownEditorService.instance = new MarkdownEditorService(githubService);
    }
    return MarkdownEditorService.instance;
  }

  public async openFile(node: Node) {
    this.node = node;
    const data: any = await this.githubService
      .getRepoContent(node.path)
      .toPromise();

    const blames: any = await this.githubService.blame(node.path).toPromise() ?? [];
    this.node.committers = [
      ...new Set(blames.map((e) => e.committer?.avatar_url)),
    ];
    this.node.blames = blames;
    this.path = node.path;
    this.node.sha = data.sha;
    this.sha = data.sha;
    this.node.content = data.content;
    this.node.url = data.download_url;
    this.setMarkdown(atobUTF8(data.content));
  }

  public saveFile(content: string, pr = false): Promise<void> {
    return new Promise(async (r, e) => {
      if (this.unsavedChanges.length) {
        (
          await this.githubService.updateFiles(this.unsavedChanges, pr)
        ).subscribe(
          () => {
            this.unsavedChanges = [];
            r();
          },
          (err) => {
            e(err);
          }
        );
      } else {
        (
          await this.githubService.updateFile(this.path, content, this.sha, pr)
        ).subscribe(
          (d: any) => {
            this.sha = d.content?.sha;
            r();
          },
          (err) => {
            e(err);
          }
        );
      }
    });
  }

  public parseMarkdown(markdown: string): string {
    // Implement your markdown parsing logic here
    return markdown; // Placeholder implementation
  }

  searchFilesInRepo(fuzzyName: string) {
    this.githubService.searchFileInRepo(fuzzyName).then((data: any) => {
      const item = data.items[0];
      if (!item) {
        return;
      }
      const node = new Node();
      node.path = item.path;
      node.sha = item.sha;
      this.openFile(node);
    });
  }

  findAll(search: string, content?: string, path?: string): any[] {
    if (!content) {
      content = this.markdown;
    }
    // find search (regex) in markdown
    return [
      ...content.matchAll(new RegExp("(.{0,3})(" + search + ")(.{0,3})", "gi")),
    ].map((e) => {
      return {
        file: false,
        match: `<i>${e[1]}</i><b>${e[2]}</b><i>${e[3]}</i>`,
        path: path || this.path,
      };
    });
  }

  async replaceAll(
    search: string,
    replace: string,
    path: string,
    index?: number
  ) {
    path = path || this.path;
    let content;
    if (path === this.path) {
      content = this.markdown;
    } else if (this.unsavedChanges.find((e) => e.path === path)) {
      content = this.unsavedChanges.find((e) => e.path === path).content;
    } else {
      content = await this.githubService.getFileContent(path);
    }
    // replace search (regex) in markdown
    if (index >= 0) {
      let i = 0;
      content = content.replace(new RegExp(search, "gi"), (e) => {
        if (i++ === index) {
          return replace;
        }
        return e;
      });
    } else {
      content = content.replace(new RegExp(search, "gi"), replace);
    }
    if (path === this.path) {
      this.markdown = content;
      this.markdownChanged.emit(this.markdown);
    }
    this.unsavedChanges.push({ path, content });
    this.unsavedChanges = _.uniqBy(this.unsavedChanges, "path");
  }

  async getOccurrences(path: string, needle: string) {
    const content = await this.githubService.getFileContent(path);
    return this.findAll(needle, content, path);
  }

  async findAllInRepo(search: string) {
    return (await this.githubService.findAll(search))?.items?.map((i: any) => ({
      file: true,
      match: i.path.split("/").pop(),
      path: i.path,
      icon: (window as any).FileIcons.getClass(i.path) || "far fa-file",
    }));
  }

  saveConfig() {
    this.githubService
      .createOrUpdateFile(
        ".gitten/config.json",
        JSON.stringify(this.config, null, 2)
      )?.then((e) => {
        e?.subscribe(() => {
          console.log("Config file saved");
        });
      });
  }

  renameFile(node: Node) {
    return new Promise<void>(async (r, e) => {
      (
        await this.githubService.createOrUpdateFile(
          node.path.replace(/[^/]+$/, node.newName),
          node.content, true
        )
      ).subscribe(
        () => {
          this.githubService
            .deleteFile(node.path, node.sha)
            .then(() => {
              r();
            });
        },
        () => {
          e();
        }
      );
    });
  }

  createFile(node: Node) {
    return new Promise<void>(async (r, e) => {
      (
        await this.githubService.createOrUpdateFile(node.path, node.content)
      ).subscribe(
        () => {
          r();
        },
        () => {
          e();
        }
      );
    });
  }
}

export function atobUTF8(base64String) {
  const binaryString = atob(base64String); // Decodifica Base64 in stringa binaria
  const bytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0)); // Converte in byte
  const utf8String = new TextDecoder().decode(bytes); // Decodifica i byte in UTF-8
  return utf8String;
}

export function btoaUTF8(utf8String) {
  const bytes = new TextEncoder().encode(utf8String); // Converte la stringa UTF-8 in byte
  const binaryString = String.fromCharCode(...bytes); // Converte i byte in stringa binaria
  const base64String = btoa(binaryString); // Codifica la stringa binaria in Base64
  return base64String;
}

export class Config {
  mediaFolder = "static";
  docsFolder = "docs";
}
